import axios from 'axios';

import { apiUrl } from 'config';

import { setRefreshTokenFailed } from '../services/app/actions';
import store from '../services/store';
import authParams from './authParams';

import tokenHelper from '../helpers/token';

export const requestConfig = {
	baseURL: apiUrl,
	json: true,
	timeout: 20000, // 20 sec
	headers: {
		Accept: 'application/json',
		'Content-Type': 'application/json',
		'cache-control': 'no-cache',
	},
};

export const api = axios.create(requestConfig);
export const apiAuth = axios.create(requestConfig);

export const getToken = async () => {
	const token = await tokenHelper.get();
	if (token) {
		await setApiAuthHeader(token.accessToken);
		return true;
	}
	return false;
};

let queueRequests = [];
let isRefreshing = false;

api.interceptors.response.use(undefined, async (error) => {
	if (isNetworkError(error)) {
		return new Promise((resolve) => {
			const originalRequest = error.config;
			setTimeout(() => {
				resolve(api(originalRequest));
			}, 1500);
		});
	}
	return Promise.reject(error);
});

apiAuth.interceptors.response.use(undefined, async (error) => {
	const originalRequest = error.config;
	if (isNetworkError(error)) {
		return new Promise((resolve) => {
			setTimeout(() => {
				resolve(api(originalRequest));
			}, 1500);
		});
	}
	if (error.response && error.response.status === 401) {
		if (!isRefreshing) {
			refreshTokenRequest();
		}

		return new Promise((resolve) => {
			queueRequests.push((token) => {
				originalRequest.headers.Authorization = generateTokenStructure(token);
				resolve(axios(originalRequest));
			});
		});
	}
	return Promise.reject(isNetworkError(error) ? 'CONNECTION_FAILED' : error);
});

export const isNetworkError = (err) => {
	return !!err.isAxiosError && !err.response;
};

const refreshTokenRequest = async () => {
	const tokens = await tokenHelper.get();
	if (tokens) {
		isRefreshing = true;
		try {
			const {
				data: { token, refreshToken },
			} = await api.post(`${apiUrl}token/createRefreshToken`, {
				...authParams,
				refreshToken: tokens.refreshToken,
			});
			isRefreshing = false;
			setToken(token, refreshToken);
			refreshQueueRequests(token);
			queueRequests = [];
		} catch (e) {
			if (e.message && e.message.indexOf('ns.GetCommandSrc') > -1) {
				// This computer has Kaspersky Internet Security 20.0.14.1085(d), disable an option called "Inject script into web traffic to interact with web pages", error gone.
				console.error(e);
				setTimeout(() => refreshTokenRequest(), 1000);
			} else {
				removeToken();
				isRefreshing = false;
				queueRequests = [];
				refreshTokenFailed();
			}
		}
	} else {
		refreshTokenFailed();
	}
};

function refreshTokenFailed() {
	store.dispatch(setRefreshTokenFailed(true));
}

export const generateTokenStructure = (token) => {
	return `Bearer ${token}`;
};

const refreshQueueRequests = (token) => {
	if (queueRequests.length === 0) return false;

	queueRequests.map((cb) => cb(token));
	return true;
};

export const setToken = (accessToken, refreshToken = null) => {
	tokenHelper.set(accessToken, refreshToken);
	setApiAuthHeader(accessToken);
};

export const setApiAuthHeader = (accessToken) => {
	apiAuth.defaults.headers.common.Authorization = generateTokenStructure(
		accessToken,
	);
};

export const removeToken = () => {
	tokenHelper.clear();
	delete apiAuth.defaults.headers.common.Authorization;
};
